import React, { useRef, useState, useEffect } from 'react';
import clsx from 'clsx';
import { ReactComponent as ChevronIcon } from '../../assets/icons/chevron-down.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/close.svg';
import { ReactComponent as CheckIcon } from '../../assets/icons/check.svg';
import { ReactComponent as LineIcon } from '../../assets/icons/line.svg';
import styles from './Select.module.scss';

interface Props {
  label: string;
  options: string[];
  value: string | null;
  onChange: (value: string | null) => void;
  allowCreate?: boolean;
  error?: string;
  className?: string;
}

export function Select({
  label,
  options,
  value,
  onChange,
  allowCreate = false,
  error,
  className
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = query
    ? options.filter((o) => o.toLowerCase().includes(query.toLowerCase()))
    : options;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleOpen() {
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function handleClose() {
    setOpen(false);
    setQuery('');
  }

  function handleChevronClick(e: React.MouseEvent) {
    e.stopPropagation();
    open ? handleClose() : handleOpen();
  }

  function handleSelect(option: string) {
    onChange(option);
    setOpen(false);
    setQuery('');
  }

  function handleCreate() {
    onChange(query.trim());
    setOpen(false);
    setQuery('');
  }

  function handleClear(e: React.MouseEvent) {
    e.stopPropagation();
    onChange(null);
    setQuery('');
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setQuery(e.target.value);
    if (!open) setOpen(true);
  }

  const displayValue = open ? query : value ?? '';
  const isFloating = !!value || open;

  return (
    <div className={clsx(styles.field, className)}>
      <div
        ref={ref}
        className={clsx(styles.wrapper, {
          [styles.open]: open,
          [styles.filled]: !!value && !open,
          [styles.wrapperError]: !!error
        })}
        onClick={!open ? handleOpen : undefined}
      >
        <label
          className={clsx(styles.label, { [styles.labelFloating]: isFloating })}
        >
          {label}
        </label>

        <input
          ref={inputRef}
          className={styles.input}
          value={displayValue}
          onChange={handleInputChange}
          placeholder={open ? 'Начните вводить или выберите из списка' : ''}
          readOnly={!open}
        />

        <div className={styles.controls}>
          {value && !open && (
            <>
              <button
                type="button"
                className={styles.clearBtn}
                onClick={handleClear}
              >
                <CloseIcon />
              </button>
              <LineIcon />
            </>
          )}
          <button
            type="button"
            className={styles.chevronBtn}
            onClick={handleChevronClick}
          >
            <ChevronIcon
              className={clsx(styles.chevron, { [styles.chevronUp]: open })}
            />
          </button>
        </div>

        {open && (
          <ul className={styles.dropdown}>
            {filtered.length > 0 ? (
              filtered.map((option) => (
                <li
                  key={option}
                  className={clsx(styles.option, {
                    [styles.optionSelected]: option === value
                  })}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(option);
                  }}
                >
                  {option}
                  <CheckIcon className={styles.optionCheck} />
                </li>
              ))
            ) : allowCreate && query.trim() ? (
              <li
                className={clsx(styles.option, styles.optionCreate)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleCreate();
                }}
              >
                Добавить: «{query.trim()}»
              </li>
            ) : (
              <li className={styles.optionEmpty}>Нет данных</li>
            )}
          </ul>
        )}
      </div>
      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
}
